import Breadcrumb from "components/Common/Breadcrumb"
import { useMemo, useState } from "react"
import {
  FaCalendarCheck,
  FaCheckCircle,
  FaEdit,
  FaPaperPlane,
  FaPlus,
  FaSave,
  FaUserFriends,
  FaUsers,
  FaWallet,
} from "react-icons/fa"
import { MdCancel, MdDelete } from "react-icons/md"
import {
  Badge,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  Col,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table,
} from "reactstrap"
import React from "react"
import Swal from "sweetalert2"
import moment from "moment"
import Select from "react-select"
import { Switch } from "antd"
import { getAllTemplatesResponse } from "utils/dummy_data"
import { loadContacts, loadGroups } from "utils/waContacts"

// TODO: replace with wallet-balance API (campaigns are paid)
const WALLET_BALANCE = 12450

const DUMMY_CAMPAIGNS = [
  {
    id: 1,
    campaignName: "Diwali Job Alerts",
    templateName: "job_notification",
    status: "Scheduled",
    recipientType: "group",
    groupId: 1,
    contactIds: [1, 2, 3],
    scheduledAt: moment().add(2, "days").format("YYYY-MM-DDTHH:mm"),
    createdAt: new Date(),
  },
  {
    id: 2,
    campaignName: "OTP Test Run",
    templateName: "verify_code_1",
    status: "Draft",
    recipientType: "contacts",
    groupId: "",
    contactIds: [4],
    scheduledAt: "",
    createdAt: new Date(),
  },
]

const EMPTY_FORM = {
  campaignName: "",
  templateName: "",
  recipientType: "contacts",
  contactIds: [],
  groupId: "",
  scheduleEnabled: false,
  scheduledAt: "",
}

const getStatusColor = status => {
  switch (status) {
    case "Sent":
      return "success"
    case "Scheduled":
      return "warning"
    case "Draft":
    default:
      return "secondary"
  }
}

// Solid backgrounds for the contacts dropdown (app CSS leaves it transparent)
const CONTACT_SELECT_STYLES = {
  control: provided => ({
    ...provided,
    backgroundColor: "#ffffff",
    borderRadius: 16,
    minHeight: 38,
  }),
  menu: provided => ({
    ...provided,
    backgroundColor: "#ffffff",
  }),
  menuList: provided => ({
    ...provided,
    backgroundColor: "#ffffff",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "#cb9019"
      : state.isFocused
        ? "#faf3e2"
        : "#ffffff",
    color: state.isSelected ? "#ffffff" : "#263a5b",
  }),
  multiValue: provided => ({
    ...provided,
    backgroundColor: "#faf3e2",
  }),
  multiValueLabel: provided => ({
    ...provided,
    color: "#6b5a33",
  }),
}

const STATS_STYLE = `
.wa-stats { display: grid; grid-template-columns: repeat(2, 1fr); }
@media (min-width: 992px) { .wa-stats { grid-template-columns: repeat(4, 1fr); } }
.wa-stat { padding: 1.15rem 1.35rem; display: flex; align-items: center; gap: 0.9rem; }
@media (min-width: 992px) { .wa-stat + .wa-stat { border-left: 1px solid #E7ECEF; } }
.wa-stat-num { font-variant-numeric: tabular-nums; }
`

const WhatsAppCampaign = () => {
  document.title = "WhatsApp Campaign | Vintage Infra Equipment Sol PVT LTD"
  const [data, setData] = useState(DUMMY_CAMPAIGNS)
  const [contacts, setContacts] = useState(() => loadContacts())
  const [groups, setGroups] = useState(() => loadGroups())
  const [pageSize, setPageSize] = useState(20)
  const [searchString, setSearchString] = useState("")
  const [currentPage, setCurrentPage] = useState(1)

  const [modalOpen, setModalOpen] = useState(false)
  const [selectedCampaign, setSelectedCampaign] = useState(EMPTY_FORM)
  const [errors, setErrors] = useState({})

  // TODO: gate with backend permission id when available (showing openly for now)

  const getContactName = id =>
    contacts.find(c => String(c.id) === String(id))?.contactName ||
    "Unknown"

  const getGroupName = id =>
    groups.find(g => String(g.id) === String(id))?.groupName || "-"

  const resolveRecipients = form => {
    if (form.recipientType === "group") {
      return (
        groups.find(g => String(g.id) === String(form.groupId))
          ?.contactIds || []
      ).map(String)
    }
    return (form.contactIds || []).map(String)
  }

  const contactOptions = useMemo(
    () =>
      contacts.map(c => ({
        value: c.id,
        label: `${c.contactName} (${c.phoneNumber})`,
      })),
    [contacts]
  )

  const stats = useMemo(
    () => [
      {
        icon: <FaUsers size={22} />,
        count: contacts.length,
        label: "Total contacts",
        tint: "#E4EDFF",
        color: "#1D5FD1",
      },
      {
        icon: <FaUserFriends size={22} />,
        count: groups.length,
        label: "Total groups",
        tint: "#DFF5F0",
        color: "#0E7C6B",
      },
      {
        icon: <FaCalendarCheck size={22} />,
        count: data.filter(c => c.status === "Scheduled").length,
        label: "Scheduled",
        tint: "#FBEFD4",
        color: "#9A6B0F",
      },
      {
        icon: <FaCheckCircle size={22} />,
        count: data.filter(c => c.status === "Sent").length,
        label: "Delivered",
        tint: "#DFF2E3",
        color: "#1C7A44",
      },
    ],
    [contacts.length, groups.length, data]
  )

  const templateOptions = useMemo(
    () => (getAllTemplatesResponse?.data || []).map(t => t.name),
    []
  )

  const previewBody = useMemo(() => {
    const template = (getAllTemplatesResponse?.data || []).find(
      t => t.name === selectedCampaign?.templateName
    )
    return template?.components?.find(c => c.type === "BODY")
  }, [selectedCampaign?.templateName])

  const filteredData = useMemo(() => {
    const search = searchString.toLowerCase()
    return data.filter(
      item =>
        item?.campaignName?.toLowerCase().includes(search) ||
        item?.templateName?.toLowerCase().includes(search) ||
        item?.status?.toLowerCase().includes(search) ||
        (item?.recipientType === "group" &&
          getGroupName(item.groupId).toLowerCase().includes(search))
    )
  }, [data, searchString, groups, contacts])

  const totalPages = Math.ceil(filteredData.length / pageSize) || 1
  const start = (currentPage - 1) * pageSize

  const paginatedData = useMemo(() => {
    const end = start + pageSize
    return filteredData.slice(start, end)
  }, [filteredData, currentPage, pageSize, start])

  const openAddModal = () => {
    setContacts(loadContacts())
    setGroups(loadGroups())
    setSelectedCampaign(EMPTY_FORM)
    setErrors({})
    setModalOpen(true)
  }

  const openEditModal = item => {
    setContacts(loadContacts())
    setGroups(loadGroups())
    setSelectedCampaign({
      ...EMPTY_FORM,
      ...item,
      contactIds: [...(item.contactIds || [])],
      scheduleEnabled: item.status === "Scheduled" && !!item.scheduledAt,
    })
    setErrors({})
    setModalOpen(true)
  }

  const validate = () => {
    const newErrors = {}
    if (!selectedCampaign?.campaignName?.trim()) {
      newErrors.campaignName = "Campaign name is required"
    }
    if (!selectedCampaign?.templateName) {
      newErrors.templateName = "Template is required"
    }
    if (selectedCampaign?.recipientType === "group") {
      if (!selectedCampaign?.groupId) {
        newErrors.groupId = "Select a group"
      } else if (resolveRecipients(selectedCampaign).length === 0) {
        newErrors.groupId = "Selected group has no contacts"
      }
    } else if (!selectedCampaign?.contactIds?.length) {
      newErrors.contactIds = "Select at least one contact"
    }
    if (selectedCampaign?.scheduleEnabled) {
      if (!selectedCampaign?.scheduledAt) {
        newErrors.scheduledAt = "Pick a future date and time"
      } else if (!moment(selectedCampaign.scheduledAt).isAfter(moment())) {
        newErrors.scheduledAt = "Schedule must be a future date and time"
      }
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Design-first: local CRUD only (no API yet)
  // Status is derived: Send Now -> Sent, scheduled -> Scheduled, else Draft
  const handleSave = sendNow => {
    if (!validate()) return

    const recipients = resolveRecipients(selectedCampaign)
    const status = sendNow
      ? "Sent"
      : selectedCampaign.scheduleEnabled && selectedCampaign.scheduledAt
        ? "Scheduled"
        : "Draft"

    const payload = {
      campaignName: selectedCampaign.campaignName.trim(),
      templateName: selectedCampaign.templateName,
      recipientType: selectedCampaign.recipientType,
      groupId:
        selectedCampaign.recipientType === "group"
          ? selectedCampaign.groupId
          : "",
      contactIds: recipients,
      scheduledAt: selectedCampaign.scheduleEnabled
        ? selectedCampaign.scheduledAt
        : "",
      status,
    }

    if (selectedCampaign?.id) {
      setData(prev =>
        prev.map(item =>
          item.id === selectedCampaign.id ? { ...item, ...payload } : item
        )
      )
    } else {
      setData(prev => [
        { ...payload, id: Date.now(), createdAt: new Date() },
        ...prev,
      ])
    }
    Swal.fire({
      title:
        status === "Sent"
          ? "Campaign Sent Successfully."
          : status === "Scheduled"
            ? "Campaign Scheduled Successfully."
            : "Campaign Saved as Draft.",
      icon: "success",
      timer: 1500,
      showConfirmButton: false,
    })
    setModalOpen(false)
    setSelectedCampaign(EMPTY_FORM)
    setCurrentPage(1)
    // TODO: replace with FetchPostData / FetchPutData "whatsapp-campaign" when API is ready
  }

  const handleDelete = item => {
    Swal.fire({
      title: `Are you sure you want to delete "${item?.campaignName}"?`,
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(result => {
      if (result.isConfirmed) {
        setData(prev => prev.filter(d => d.id !== item.id))
        Swal.fire({
          title: "Campaign Deleted Successfully.",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        })
        // TODO: replace with FetchDeleteData "whatsapp-campaign" when API is ready
      }
    })
  }

  const renderRecipients = item => {
    if (item.recipientType === "group") {
      return (
        <span>
          <span className="fw-bold">{getGroupName(item.groupId)}</span>{" "}
          <span className="text-muted small">
            ({item.contactIds?.length || 0})
          </span>
        </span>
      )
    }
    const n = item.contactIds?.length || 0
    return (
      <span>
        {n} contact{n === 1 ? "" : "s"}
      </span>
    )
  }

  return (
    <div className="page-content">
      <style>{STATS_STYLE}</style>
      <Breadcrumb title="WhatsApp" breadcrumbItem="Campaign" />

      {/* Command panel */}
      <div
        className="mb-3 px-4 py-4"
        style={{
          borderRadius: 20,
          color: "#F2F7F4",
          background:
            "radial-gradient(560px 220px at 88% 10%, rgba(37,211,102,.28), transparent 70%), linear-gradient(115deg, #0A3527 0%, #0E4D33 100%)",
        }}
      >
        <Row className="align-items-center g-3">
          <Col lg={7}>
            <h3 className="mb-1 fw-bold" style={{ letterSpacing: "-0.01em" }}>
              Campaigns
            </h3>
            <p className="mb-0" style={{ color: "#BCD8C9", maxWidth: 560 }}>
              Broadcast approved templates to contacts and groups, then track
              scheduled and delivered sends in one place.
            </p>
          </Col>
          <Col lg={5}>
            <div className="d-flex align-items-center justify-content-lg-end gap-3 flex-wrap">
              <div
                className="d-flex align-items-center gap-3 px-3 py-2"
                style={{
                  borderRadius: 16,
                  background: "rgba(255,255,255,.08)",
                  border: "1px solid rgba(255,255,255,.16)",
                }}
              >
                <span
                  className="d-flex align-items-center justify-content-center flex-shrink-0"
                  style={{
                    width: 46,
                    height: 46,
                    borderRadius: 13,
                    background: "#25D366",
                    color: "#0A3527",
                  }}
                >
                  <FaWallet size={22} />
                </span>
                <span>
                  <span
                    className="d-block small"
                    style={{ color: "#BCD8C9" }}
                  >
                    Wallet balance
                  </span>
                  <span
                    className="d-block fw-bold wa-stat-num"
                    style={{ fontSize: "1.65rem", lineHeight: 1.1 }}
                  >
                    ₹{WALLET_BALANCE.toLocaleString("en-IN")}
                  </span>
                  <span
                    className="d-block small mt-1"
                    style={{ color: "#BCD8C9" }}
                  >
                    Each sent message bills this balance
                  </span>
                </span>
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* Stat strip */}
      <Card className="shadow-lg mb-3">
        <div className="wa-stats">
          {stats.map(s => (
            <div key={s.label} className="wa-stat">
              <span
                className="d-flex align-items-center justify-content-center flex-shrink-0"
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  backgroundColor: s.tint,
                  color: s.color,
                }}
              >
                {s.icon}
              </span>
              <span>
                <span
                  className="d-block fw-bold wa-stat-num"
                  style={{ fontSize: "1.7rem", lineHeight: 1.1 }}
                >
                  {s.count}
                </span>
                <span className="d-block text-muted small">{s.label}</span>
              </span>
            </div>
          ))}
        </div>
      </Card>

      <Card className="shadow-lg">
        <CardBody>
          <h5 className="fw-bold mb-3">All campaigns</h5>
          {/* Top controls */}
          <div className="d-flex justify-content-between align-items-center gap-2 mb-3 flex-nowrap">
            <div className="d-flex gap-2 align-items-center flex-nowrap flex-grow-1">
              <Input
                type="select"
                value={pageSize}
                onChange={e => {
                  setPageSize(Number(e.target.value)), setCurrentPage(1)
                }}
                className="rounded-4 pe-5 flex-shrink-0"
                style={{ width: "90px" }}
              >
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Input>

              <Input
                className="rounded-4 flex-grow-1"
                style={{ maxWidth: "320px" }}
                placeholder="Search Campaigns"
                value={searchString}
                onChange={e => {
                  setSearchString(e.target.value)
                  setCurrentPage(1)
                }}
              />
            </div>
            <Button
              className="btn-blue rounded-4 px-3 flex-shrink-0 text-nowrap"
              onClick={openAddModal}
            >
              <FaPlus className="me-2" />
              New campaign
            </Button>
          </div>

          {/* Table */}
          <Table bordered responsive className="align-middle">
            <thead className="table-dark">
              <tr>
                <th className="text-center" style={{ width: "70px" }}>
                  #
                </th>
                <th style={{ minWidth: "200px" }}>Campaign Name</th>
                <th style={{ minWidth: "180px" }}>Template</th>
                <th style={{ minWidth: "170px" }}>Recipients</th>
                <th className="text-center" style={{ width: "150px" }}>
                  Status
                </th>
                <th style={{ width: "170px" }}>Scheduled At</th>
                <th className="text-center" style={{ width: "140px" }}>
                  Action
                </th>
              </tr>
            </thead>

            <tbody>
              {paginatedData.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center text-muted py-4">
                    No campaigns yet. Use New campaign to send your first
                    broadcast.
                  </td>
                </tr>
              ) : (
                paginatedData.map((item, index) => (
                  <tr key={item.id}>
                    <td className="text-center">{start + index + 1}</td>
                    <td>
                      <span className="fw-bold">{item.campaignName}</span>
                    </td>
                    <td>{item.templateName}</td>
                    <td>{renderRecipients(item)}</td>
                    <td className="text-center">
                      <Badge
                        pill
                        color={getStatusColor(item.status)}
                        style={{ fontSize: "0.9rem", padding: "0.45em 1em" }}
                      >
                        {item.status}
                      </Badge>
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {item.scheduledAt
                        ? moment(item.scheduledAt).format("DD-MM-YYYY hh:mm A")
                        : "-"}
                    </td>
                    <td className="text-center" style={{ whiteSpace: "nowrap" }}>
                      <Button
                        className="bg-success text-white me-2 border-0 shadow-none"
                        title="Edit campaign"
                        onClick={() => openEditModal(item)}
                      >
                        <FaEdit size={16} />
                      </Button>
                      <Button
                        className="bg-danger text-white border-0 shadow-none"
                        title="Delete campaign"
                        onClick={() => handleDelete(item)}
                      >
                        <MdDelete size={18} />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
          <nav
            aria-label="Page navigation example"
            className="d-flex justify-content-between mt-3"
          >
            <h5>
              Showing {filteredData.length === 0 ? 0 : start + 1} to{" "}
              {Math.min(start + pageSize, filteredData.length)} of{" "}
              {filteredData.length} entries
            </h5>
            <ul className="pagination mb-0">
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                >
                  Previous
                </button>
              </li>

              {[...Array(totalPages)].map((_, i) => {
                const pageNum = i + 1
                return (
                  <li
                    key={pageNum}
                    className={`page-item ${
                      pageNum === currentPage ? "active" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  </li>
                )
              })}

              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() =>
                    setCurrentPage(prev => Math.min(prev + 1, totalPages))
                  }
                >
                  Next
                </button>
              </li>
            </ul>
          </nav>
        </CardBody>
      </Card>

      <Modal
        isOpen={modalOpen}
        toggle={() => setModalOpen(false)}
        fullscreen
      >
        <ModalHeader toggle={() => setModalOpen(false)}>
          {selectedCampaign?.id ? "Edit" : "New"} campaign
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col lg={6}>
          <div className="mb-3">
            <Label>
              Campaign Name <span className="text-danger">*</span>
            </Label>
            <Input
              placeholder="e.g. Diwali Job Alerts"
              value={selectedCampaign?.campaignName || ""}
              invalid={!!errors.campaignName}
              onChange={e =>
                setSelectedCampaign(prev => ({
                  ...prev,
                  campaignName: e.target.value,
                }))
              }
            />
            {errors.campaignName && (
              <FormFeedback className="d-block">
                {errors.campaignName}
              </FormFeedback>
            )}
          </div>

          <div className="mb-3">
            <Label>
              Template <span className="text-danger">*</span>
            </Label>
            <Input
              type="select"
              value={selectedCampaign?.templateName || ""}
              invalid={!!errors.templateName}
              onChange={e =>
                setSelectedCampaign(prev => ({
                  ...prev,
                  templateName: e.target.value,
                }))
              }
            >
              <option value="">Select template</option>
              {templateOptions.map(name => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </Input>
            {errors.templateName && (
              <FormFeedback className="d-block">
                {errors.templateName}
              </FormFeedback>
            )}
          </div>

          <div className="mb-3">
            <Label>
              Send To <span className="text-danger">*</span>
            </Label>
            <ButtonGroup className="d-flex">
              <Button
                color="primary"
                outline={selectedCampaign?.recipientType !== "contacts"}
                className="w-100"
                onClick={() =>
                  setSelectedCampaign(prev => ({
                    ...prev,
                    recipientType: "contacts",
                  }))
                }
              >
                Individual Contacts
              </Button>
              <Button
                color="primary"
                outline={selectedCampaign?.recipientType !== "group"}
                className="w-100"
                onClick={() =>
                  setSelectedCampaign(prev => ({
                    ...prev,
                    recipientType: "group",
                  }))
                }
              >
                Group
              </Button>
            </ButtonGroup>
          </div>

          {selectedCampaign?.recipientType === "group" ? (
            <div className="mb-3">
              <Label>
                Group <span className="text-danger">*</span>
              </Label>
              <Input
                type="select"
                value={selectedCampaign?.groupId || ""}
                invalid={!!errors.groupId}
                onChange={e =>
                  setSelectedCampaign(prev => ({
                    ...prev,
                    groupId: e.target.value,
                  }))
                }
              >
                <option value="">
                  {groups.length
                    ? "Select group"
                    : "No groups yet — create one in Groups first"}
                </option>
                {groups.map(g => (
                  <option key={g.id} value={g.id}>
                    {g.groupName} ({g.contactIds?.length || 0})
                  </option>
                ))}
              </Input>
              {errors.groupId ? (
                <FormFeedback className="d-block">
                  {errors.groupId}
                </FormFeedback>
              ) : (
                selectedCampaign?.groupId && (
                  <small className="text-muted">
                    {resolveRecipients(selectedCampaign).length} contact(s)
                    from this group will receive the campaign.
                  </small>
                )
              )}
            </div>
          ) : (
            <div className="mb-3">
              <Label>
                Contacts <span className="text-danger">*</span>
              </Label>
              <Select
                isMulti
                options={contactOptions}
                placeholder={
                  contactOptions.length
                    ? "Select contacts"
                    : "No contacts yet — add some in Contacts first"
                }
                styles={CONTACT_SELECT_STYLES}
                value={contactOptions.filter(opt =>
                  (selectedCampaign?.contactIds || [])
                    .map(String)
                    .includes(String(opt.value))
                )}
                onChange={selected =>
                  setSelectedCampaign(prev => ({
                    ...prev,
                    contactIds: (selected || []).map(opt => opt.value),
                  }))
                }
                className={errors.contactIds ? "is-invalid" : ""}
              />
              {errors.contactIds && (
                <div className="invalid-feedback d-block">
                  {errors.contactIds}
                </div>
              )}
            </div>
          )}

          <div className="d-flex align-items-center justify-content-between border rounded-4 px-3 py-2 mb-3 bg-light">
            <div>
              <div className="fw-bold">Schedule for later</div>
              <small className="text-muted">
                Turn on to pick a future date and time
              </small>
            </div>
            <Switch
              checked={!!selectedCampaign?.scheduleEnabled}
              onChange={checked =>
                setSelectedCampaign(prev => ({
                  ...prev,
                  scheduleEnabled: checked,
                  scheduledAt: checked ? prev.scheduledAt : "",
                }))
              }
            />
          </div>

          {selectedCampaign?.scheduleEnabled && (
            <div className="mb-3">
              <Label>
                Scheduled At <span className="text-danger">*</span>
              </Label>
              <Input
                type="datetime-local"
                min={moment().format("YYYY-MM-DDTHH:mm")}
                value={selectedCampaign?.scheduledAt || ""}
                invalid={!!errors.scheduledAt}
                onChange={e =>
                  setSelectedCampaign(prev => ({
                    ...prev,
                    scheduledAt: e.target.value,
                  }))
                }
              />
              {errors.scheduledAt && (
                <FormFeedback className="d-block">
                  {errors.scheduledAt}
                </FormFeedback>
              )}
            </div>
          )}

            </Col>
            <Col lg={6}>
          <div className="border rounded-4 p-3 bg-white h-100">
            <div className="fw-bold mb-2">Template preview</div>
            {previewBody ? (
              <>
                <div className="small" style={{ whiteSpace: "pre-wrap" }}>
                  {previewBody.text}
                </div>
                {previewBody.example?.body_text?.[0] && (
                  <div className="mt-2">
                    {previewBody.example.body_text[0].map((val, idx) => (
                      <div
                        key={idx}
                        className="border rounded-3 px-2 py-1 mb-1 small bg-light"
                      >
                        <span className="fw-bold text-muted">
                          {"{{" + (idx + 1) + "}}"}:{" "}
                        </span>
                        {val}
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <small className="text-muted">
                Select a template above to preview its message here.
              </small>
            )}
          </div>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter className="d-flex justify-content-end gap-2 flex-wrap">
          {selectedCampaign?.scheduleEnabled ? (
            <Button className="btn-blue" onClick={() => handleSave(false)}>
              <FaCalendarCheck className="me-1" /> Save &amp; Schedule
            </Button>
          ) : (
            <>
              <Button className="btn-blue" onClick={() => handleSave(false)}>
                <FaSave className="me-1" /> Save &amp; Send Later
              </Button>
              <Button color="success" onClick={() => handleSave(true)}>
                <FaPaperPlane className="me-1" /> Send Now
              </Button>
            </>
          )}
          <Button className="btn-danger" onClick={() => setModalOpen(false)}>
            <MdCancel className="me-1" /> Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default WhatsAppCampaign
